import React from 'react';
import {
  Box, Button, Divider, Grid, IconButton, MenuItem, TextField, Tooltip, Typography,
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';

// Keep in sync with server/model/LoanOfficer.js
export const MAX_REVIEWS = 10;
export const REVIEW_TEXT_MAX = 250;

const RANKS = Array.from({ length: MAX_REVIEWS }, (_, i) => i + 1);

let lastKey = 0;
const newKey = () => {
  lastKey += 1;
  return `review-${lastKey}`;
};

const blankReview = (rank = '') => ({ key: newKey(), name: '', location: '', text: '', rank });

const isBlank = (r) => !r.name.trim() && !r.location.trim() && !r.text.trim();

// Turn an LO's saved reviews into editor blocks. The form always shows at
// least one block, even when the LO has no reviews yet.
export function reviewsToBlocks(reviews) {
  const blocks = (reviews || []).map(({ name = '', location = '', text = '', rank = '' }) => ({
    key: newKey(), name, location, text, rank,
  }));
  return blocks.length ? blocks : [blankReview(1)];
}

// Check the blocks before saving. Blank blocks are dropped. Returns
// { reviews } ready to send, or { error } naming the first problem.
export function blocksToReviews(blocks) {
  const reviews = [];
  const ranks = new Set();

  for (let i = 0; i < blocks.length; i += 1) {
    const b = blocks[i];
    if (!isBlank(b)) {
      const label = `Review ${i + 1}`;
      const missing = [
        !b.name.trim() && 'First Name, Last Initial',
        !b.location.trim() && 'City, State',
        !b.text.trim() && 'Review',
        !b.rank && 'Rank Placement',
      ].filter(Boolean);

      if (missing.length) return { error: `${label} is missing: ${missing.join(', ')}.` };

      const rank = Number(b.rank);
      if (ranks.has(rank)) return { error: `Two reviews share rank ${rank}.` };
      ranks.add(rank);

      reviews.push({
        name: b.name.trim(),
        location: b.location.trim(),
        text: b.text.trim(),
        rank,
      });
    }
  }

  return { reviews };
}

export default function ReviewsEditor({ blocks, onChange }) {
  const atMax = blocks.length >= MAX_REVIEWS;

  const ranksTakenBy = (exceptKey) =>
    new Set(blocks.filter((b) => b.key !== exceptKey).map((b) => Number(b.rank)).filter(Boolean));

  const update = (key, field, value) =>
    onChange(blocks.map((b) => (b.key === key ? { ...b, [field]: value } : b)));

  // New blocks go directly above the block whose + was clicked.
  const addAbove = (index) => {
    if (atMax) return;
    const taken = ranksTakenBy(null);
    const rank = RANKS.find((n) => !taken.has(n)) || '';
    onChange([...blocks.slice(0, index), blankReview(rank), ...blocks.slice(index)]);
  };

  // Deleting the only block clears it instead, so there is always one to fill in.
  const remove = (key) =>
    onChange(blocks.length === 1 ? [blankReview(1)] : blocks.filter((b) => b.key !== key));

  return (
    <Box>
      <Typography variant="h6">Reviews</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Shown on the LO&apos;s page in rank order (1 first). Up to {MAX_REVIEWS} reviews.
        Changes take effect when you click Save Changes.
      </Typography>

      {blocks.map((b, index) => {
        const taken = ranksTakenBy(b.key);
        return (
          <Box key={b.key}>
            {index > 0 && <Divider sx={{ my: 3 }} />}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name, Last Initial"
                  placeholder="Sarah B."
                  value={b.name}
                  onChange={(e) => update(b.key, 'name', e.target.value)}
                  inputProps={{ maxLength: 60 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City, State"
                  placeholder="Jacksonville, FL"
                  value={b.location}
                  onChange={(e) => update(b.key, 'location', e.target.value)}
                  inputProps={{ maxLength: 60 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label={`Review (max ${REVIEW_TEXT_MAX} characters)`}
                  value={b.text}
                  onChange={(e) => update(b.key, 'text', e.target.value)}
                  inputProps={{ maxLength: REVIEW_TEXT_MAX }}
                  helperText={`${b.text.length} / ${REVIEW_TEXT_MAX}`}
                  FormHelperTextProps={{ sx: { textAlign: 'right' } }}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  select
                  fullWidth
                  label={`Rank Placement (max ${MAX_REVIEWS})`}
                  value={b.rank}
                  onChange={(e) => update(b.key, 'rank', e.target.value)}
                >
                  {RANKS.map((n) => (
                    <MenuItem key={n} value={n} disabled={taken.has(n)}>
                      {n}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid
                item
                xs={12}
                sm={7}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}
              >
                <Button variant="contained" color="error" size="small" onClick={() => remove(b.key)}>
                  Delete Review
                </Button>
                <Tooltip title={atMax ? `Maximum ${MAX_REVIEWS} reviews` : 'Add a review above this one'}>
                  <span>
                    <IconButton
                      color="primary"
                      aria-label="Add a review above this one"
                      disabled={atMax}
                      onClick={() => addAbove(index)}
                    >
                      <AddCircleIcon sx={{ fontSize: 36 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>
        );
      })}
    </Box>
  );
}
